import { useCallback, useEffect, useState } from 'react';
import { applyUpdate, getWaitingRegistration } from '../serviceWorkerRegistration';

/**
 * Tracks whether a new build is waiting to be applied.
 *
 * - needRefresh: a new service worker is installed and waiting in this tab.
 * - activatedElsewhere: another tab applied the update, so this tab is running
 *   an outdated bundle until it reloads.
 *
 * Dismissing hides the prompt until the app is next brought to the foreground,
 * so a PWA that is never fully closed still converges on the new build.
 */
export default function useServiceWorkerUpdate() {
  const [registration, setRegistration] = useState(() => getWaitingRegistration());
  const [activatedElsewhere, setActivatedElsewhere] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleAvailable = (event) => {
      setRegistration(event?.detail?.registration || getWaitingRegistration());
      setDismissed(false);
    };
    const handleActivatedElsewhere = () => {
      setActivatedElsewhere(true);
      setDismissed(false);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') setDismissed(false);
    };

    window.addEventListener('civiccode:sw-update-available', handleAvailable);
    window.addEventListener('civiccode:sw-activated-elsewhere', handleActivatedElsewhere);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('civiccode:sw-update-available', handleAvailable);
      window.removeEventListener('civiccode:sw-activated-elsewhere', handleActivatedElsewhere);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const update = useCallback(() => {
    if (activatedElsewhere) {
      window.location.reload();
      return;
    }
    applyUpdate(registration || undefined);
  }, [activatedElsewhere, registration]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return {
    needRefresh: Boolean(registration) || activatedElsewhere,
    activatedElsewhere,
    dismissed,
    update,
    dismiss,
  };
}
